import { Form, Formik } from "formik";
import { useState } from "react";
import { Alert, Form as BootstrapForm } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { LocationMapAddressFields } from "../../components/features/location/LocationMapAddressFields";
import { EditFormSaveCancelButtons, EditFormSplitFooter } from "../../components/Forms";
import type { LocationCreatePayload } from "../../customTypings/location/Location";
import { useGoogleMapsConfig } from "../../hooks/useGoogleMapsConfig";
import { locationsService } from "../../services/locations/locations.service";
import {
    createEditFormSubmitHandler,
    DEFAULT_FORM_SAVE_ERROR_TOAST_MESSAGE,
    optionalNullableString,
    hydratePostalAddressForForm,
    mapPostalAddressToApi,
    showToastError,
    showToastSuccess,
} from "../../utils";
import { formatLocationToastLabel } from "../LocationsListPage/locationAdminListActions";
import { type LocationFormValues } from "../LocationEditPage/LocationEditFormGrid";
import { useCustomerEditOutletContext } from "./customerEditOutletContext";

const coordinateField = Yup.number()
    .transform((_value, originalValue) => {
        if (originalValue === "" || originalValue === null || originalValue === undefined) {
            return undefined;
        }
        return Number(originalValue);
    })
    .typeError("Please pick a point on the map or search for an address.")
    .required("Please pick a point on the map or search for an address.");

const validationSchema = Yup.object({
    name: Yup.string()
        .transform((v) => (typeof v === "string" ? v.trim() : v))
        .required("Name is required.")
        .max(255, "Name must be at most 255 characters."),
    addressLine1: optionalNullableString,
    addressLine2: optionalNullableString,
    addressLine3: optionalNullableString,
    town: optionalNullableString,
    county: optionalNullableString,
    postcode: optionalNullableString,
    country: optionalNullableString,
    latitude: coordinateField,
    longitude: coordinateField,
    isEnabled: Yup.boolean().required(),
});

const emptyForm: LocationFormValues = {
    name: "",
    ...hydratePostalAddressForForm({}),
    latitude: null,
    longitude: null,
    isEnabled: true,
};

const CustomerNewLocationPage = () => {
    const navigate = useNavigate();
    const { customerId, customerName } = useCustomerEditOutletContext();
    const { config: mapConfig, loading: mapConfigLoading, error: mapConfigError } = useGoogleMapsConfig();
    const mapsEnabled =
        mapConfig?.enabled === true && Boolean(mapConfig.browserApiKey) && Boolean(mapConfig.mapId);
    const [initialValues] = useState<LocationFormValues>(emptyForm);
    const [liveValidationAfterSubmit, setLiveValidationAfterSubmit] = useState(false);
    const [isMainLocation, setIsMainLocation] = useState(false);

    if (mapConfigLoading) {
        return null;
    }

    return (
        <>
            <h3 className="mt-4 mb-3">Create New {customerName} Location</h3>
            {mapConfigError && (
                <Alert variant="danger" className="mb-3">
                    {mapConfigError}
                </Alert>
            )}

            {!mapsEnabled && (
                <Alert variant="warning" className="mb-3">
                    Maps are not set up for this system. You cannot use the map or address search until an administrator enables maps.
                    {" "}You cannot create a new location until maps are enabled and coordinates can be set.
                </Alert>
            )}

            <Formik<LocationFormValues>
                enableReinitialize
                initialValues={initialValues}
                validationSchema={validationSchema}
                validateOnMount={false}
                validateOnChange
                validateOnBlur
                onSubmit={async (values) => {
                    if (!mapsEnabled) {
                        showToastError("Maps must be turned on before you can create a location on the map.");
                        return;
                    }
                    setLiveValidationAfterSubmit(true);
                    try {
                        const addressPayload = mapPostalAddressToApi(values);
                        const createPayload: LocationCreatePayload = {
                            name: values.name.trim(),
                            latitude: values.latitude!,
                            longitude: values.longitude!,
                            isEnabled: values.isEnabled,
                            ...addressPayload,
                            initialCustomerAssociation: {
                                customerId,
                                isMainLocation,
                            },
                        };
                        await locationsService.create(createPayload);
                        setLiveValidationAfterSubmit(false);
                        showToastSuccess(`${formatLocationToastLabel(values.name)} created successfully!`);
                        navigate(`/customers/${customerId}/locations`, { replace: true });
                    } catch {
                        showToastError(DEFAULT_FORM_SAVE_ERROR_TOAST_MESSAGE);
                    }
                }}
            >
                {({ isSubmitting, values, submitCount, handleSubmit, validateForm, setTouched }) => {
                    const createSaveBlocked =
                        !mapsEnabled || values.latitude == null || values.longitude == null;

                    return (
                        <Form
                            noValidate
                            onSubmit={createEditFormSubmitHandler(validateForm, setTouched, values, handleSubmit)}
                        >
                            <LocationMapAddressFields
                                mapConfig={mapConfig}
                                mapsEnabled={mapsEnabled}
                                metaDeleted={false}
                                showNameField
                                showLocationEnabledField
                                isNew
                                liveValidationAfterSubmit={liveValidationAfterSubmit}
                                submitCount={submitCount}
                                coordinatesRowExtra={
                                    <BootstrapForm.Check
                                        type="checkbox"
                                        id="customer-new-location-main"
                                        className="text-nowrap"
                                        style={{ marginBottom: "6px" }}
                                        label={`Set as primary address for ${customerName}`}
                                        checked={isMainLocation}
                                        onChange={(e) => setIsMainLocation(e.target.checked)}
                                    />
                                }
                            />

                            <EditFormSplitFooter>
                                <EditFormSaveCancelButtons
                                    isSubmitting={isSubmitting}
                                    disabled={createSaveBlocked}
                                    saveLabel="Create"
                                    onCancel={() => navigate(`/customers/${customerId}/locations`)}
                                />
                            </EditFormSplitFooter>
                        </Form>
                    );
                }}
            </Formik>
        </>
    );
};

export { CustomerNewLocationPage };
